import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { useAuth } from './AuthContext';
import CartService from '../services/cartService';
import type { ShippingAddress, PaymentDetails } from '../services/cartService';
import { Snackbar, Alert, Button, Stack, Typography } from '@mui/material';

// Define the cart item interface with strict typing
export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  unit: string;
  stock?: number | null;
  seller: {
    name: string;
    location: string;
  };
  shop_id?: string | null;
  seller_id?: string | null;
}

// Define the cart context interface
interface CartContextType {
  cartItems: CartItem[];
  loading: boolean;
  addToCart: (product: Omit<CartItem, 'quantity'>, quantity?: number) => Promise<void>;
  removeFromCart: (productId: string) => Promise<void>;
  updateQuantity: (productId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  getCartTotal: () => number;
  getItemCount: () => number;
  isInCart: (productId: string) => boolean;
  checkout: (shippingAddress: ShippingAddress, paymentDetails: PaymentDetails) => Promise<{ success: boolean; orderId?: string; error?: string }>;
  refreshCart: () => Promise<void>;
}

type CartNotice = {
  open: boolean;
  severity: 'success' | 'warning';
  title: string;
  message: string;
};

// Create the cart context with default values
const CartContext = createContext<CartContextType>({
  cartItems: [],
  loading: false,
  addToCart: async () => {},
  removeFromCart: async () => {},
  updateQuantity: async () => {},
  clearCart: async () => {},
  getCartTotal: () => 0,
  getItemCount: () => 0,
  isInCart: () => false,
  checkout: async () => ({ success: false, error: 'Not implemented' }),
  refreshCart: async () => {},
});

// Custom hook to use the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Cart provider component
export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hydratedScope, setHydratedScope] = useState<string | null>(null);
  const [cartNotice, setCartNotice] = useState<CartNotice>({
    open: false,
    severity: 'success',
    title: '',
    message: '',
  });
  const cartScope = isAuthenticated && user?.id ? `auth:${user.id}` : 'guest';
  const initializingScopeRef = useRef<string | null>(null);

  const openCartNotice = (notice: Omit<CartNotice, 'open'>) => {
    setCartNotice({
      open: true,
      ...notice,
    });
  };

  // Initialize cart on component mount and auth state change
  const dedupeCartItems = (items: CartItem[], strategy: 'sum' | 'max' = 'sum'): CartItem[] => {
    const merged: Record<string, CartItem> = {};

    items.forEach((item) => {
      if (!item?.id) return;

      if (merged[item.id]) {
        merged[item.id] = {
          ...merged[item.id],
          ...item,
          quantity:
            strategy === 'max'
              ? Math.max(merged[item.id].quantity, item.quantity)
              : merged[item.id].quantity + item.quantity,
          stock: item.stock ?? merged[item.id].stock ?? null,
        };
      } else {
        merged[item.id] = { ...item };
      }
    });

    return Object.values(merged);
  };

  useEffect(() => {
    const initializeCart = async () => {
      initializingScopeRef.current = cartScope;
      setLoading(true);
      try {
        if (isAuthenticated && user?.id) {
          // Load cart from database for authenticated users
          const dbCartItems = await CartService.loadCartFromDatabase(user.id);
          
          // Merge with localStorage cart if exists
          const localCart = getLocalStorageCart();
          if (localCart.length > 0) {
            // When auth cart and guest cart overlap, prefer the higher quantity
            // to avoid doubling items after refresh/login hydration.
            const mergedCart = dedupeCartItems([...dbCartItems, ...localCart], 'max');
            setCartItems(mergedCart);
            
            // Save merged cart to database
            await CartService.saveCartToDatabase(user.id, mergedCart);
            
            // Clear localStorage after merging
            clearLocalStorageCart();
          } else {
            setCartItems(dbCartItems);
          }
        } else {
          // Load from localStorage for non-authenticated users
          const localCart = getLocalStorageCart();
          setCartItems(localCart);
        }
      } catch (error) {
        console.error('Error initializing cart:', error);
        // Fallback to localStorage
        const localCart = getLocalStorageCart();
        setCartItems(localCart);
      } finally {
        setLoading(false);
        setHydratedScope(cartScope);
        initializingScopeRef.current = null;
      }
    };

    if (hydratedScope !== cartScope && initializingScopeRef.current !== cartScope) {
      initializeCart();
    }
  }, [cartScope, hydratedScope, isAuthenticated, user?.id]);

  // Save cart items to appropriate storage whenever they change
  useEffect(() => {
    if (hydratedScope !== cartScope) return;
    if (initializingScopeRef.current === cartScope) return;

    const saveCart = async () => {
      try {
        if (isAuthenticated && user?.id) {
          // Save to database for authenticated users
          await CartService.saveCartToDatabase(user.id, cartItems);
        } else {
          // Save to localStorage for non-authenticated users
          saveToLocalStorage(cartItems);
        }
      } catch (error) {
        console.error('Error saving cart:', error);
        // Fallback to localStorage
        saveToLocalStorage(cartItems);
      }
    };

    saveCart();
  }, [cartItems, cartScope, hydratedScope, isAuthenticated, user?.id]);

  // Helper functions for localStorage operations
  const getLocalStorageCart = (): CartItem[] => {
    if (typeof window !== 'undefined') {
      try {
        const savedCart = localStorage.getItem('farmers_hub_cart');
        if (!savedCart) return [];
        
        const parsedCart = JSON.parse(savedCart);
        
        // Filter out invalid items (e.g. legacy items with number IDs)
        if (Array.isArray(parsedCart)) {
          return parsedCart.filter(item => 
            item && 
            typeof item.id === 'string' && 
            item.id.length > 0 && 
            // Check if it's a valid UUID (simple check) or at least not a simple number string
            // Actually, for now, just ensure it is a string. If it's "1", it might still fail if passed to UUID column.
            // Let's rely on the fact that new items are UUIDs.
            // If we encounter "1", it's legacy.
            isNaN(Number(item.id)) 
          );
        }
        return [];
      } catch (error) {
        console.error('Error reading cart from localStorage:', error);
        return [];
      }
    }
    return [];
  };

  const saveToLocalStorage = (items: CartItem[]) => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('farmers_hub_cart', JSON.stringify(items));
      } catch (error) {
        console.error('Error saving cart to localStorage:', error);
      }
    }
  };

  const clearLocalStorageCart = () => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('farmers_hub_cart');
      } catch (error) {
        console.error('Error clearing cart from localStorage:', error);
      }
    }
  };

  // Add a product to the cart
  const addToCart = async (product: any, quantity: number = 1) => {
    setLoading(true);
    try {
      const productName = String(product?.name || 'Product');
      const requestedQuantity = Math.max(1, Number(quantity) || 1);
      const normalizedUnit = String(product?.unit || 'item');
      const normalizedStock =
        product?.stock === null || product?.stock === undefined || product?.stock === ''
          ? null
          : Math.max(0, Number(product.stock) || 0);

      if (normalizedStock === 0) {
        openCartNotice({
          severity: 'warning',
          title: 'Out of stock',
          message: `${productName} is currently unavailable for purchase.`,
        });
        return;
      }

      let resolvedQuantity = requestedQuantity;
      let wasClamped = false;

      setCartItems(prevItems => {
        // Ensure ID is string
        const productId = product.id.toString();
        const existingItem = prevItems.find(item => item.id === productId);
        const sellerName = String(product?.seller?.name || product?.seller_name || product?.shop_name || 'Marketplace Seller');
        const sellerLocation = String(product?.seller?.location || product?.seller_location || product?.location || 'Kenya');
        const normalizedImage = String(product?.image || product?.images?.[0] || '');
        const normalizedPrice = Number(product?.price || 0);
        const nextMaxStock = normalizedStock ?? existingItem?.stock ?? null;

        if (existingItem) {
          const desiredQuantity = existingItem.quantity + requestedQuantity;
          const finalQuantity =
            nextMaxStock !== null
              ? Math.min(desiredQuantity, nextMaxStock)
              : desiredQuantity;
          resolvedQuantity = finalQuantity - existingItem.quantity;
          wasClamped = finalQuantity !== desiredQuantity;

          if (resolvedQuantity <= 0) {
            return prevItems;
          }

          return prevItems.map(item =>
            item.id === productId
              ? { ...item, quantity: finalQuantity, stock: nextMaxStock }
              : item
          );
        }

        const finalQuantity =
          nextMaxStock !== null
            ? Math.min(requestedQuantity, nextMaxStock)
            : requestedQuantity;
        resolvedQuantity = finalQuantity;
        wasClamped = finalQuantity !== requestedQuantity;

        if (finalQuantity <= 0) {
          return prevItems;
        }

        return [...prevItems, {
          id: productId,
          name: String(product?.name || 'Product'),
          price: Number.isFinite(normalizedPrice) ? normalizedPrice : 0,
          quantity: finalQuantity,
          image: normalizedImage,
          unit: normalizedUnit,
          stock: nextMaxStock,
          seller: {
            name: sellerName,
            location: sellerLocation,
          },
          shop_id: product?.shop_id ? String(product.shop_id) : null,
          seller_id: product?.seller?.id ? String(product.seller.id) : (product?.seller_id ? String(product.seller_id) : null),
        }];
      });

      if (resolvedQuantity <= 0) {
        openCartNotice({
          severity: 'warning',
          title: 'Cart limit reached',
          message: `You already have the maximum available quantity for ${productName}.`,
        });
        return;
      }

      if (wasClamped && normalizedStock !== null) {
        openCartNotice({
          severity: 'warning',
          title: 'Stock limit applied',
          message: `Only ${normalizedStock} ${normalizedUnit}${normalizedStock === 1 ? '' : 's'} available for ${productName}.`,
        });
        return;
      }

      openCartNotice({
        severity: 'success',
        title: 'Added to cart',
        message:
          resolvedQuantity > 1
            ? `${resolvedQuantity} x ${productName} added successfully.`
            : `${productName} added successfully.`,
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Remove a product from the cart
  const removeFromCart = async (productId: string) => {
    setLoading(true);
    try {
      setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update the quantity of a product in the cart
  const updateQuantity = async (productId: string, quantity: number) => {
    setLoading(true);
    try {
      if (quantity <= 0) {
        await removeFromCart(productId);
        return;
      }

      let appliedClamp = false;

      setCartItems(prevItems =>
        prevItems.map(item => {
          if (item.id !== productId) return item;

          const maxStock =
            item.stock === null || item.stock === undefined
              ? null
              : Math.max(0, Number(item.stock) || 0);

          if (maxStock !== null && maxStock > 0 && quantity > maxStock) {
            appliedClamp = true;
            return { ...item, quantity: maxStock };
          }

          if (maxStock === 0) {
            appliedClamp = true;
            return { ...item, quantity: 1 };
          }

          return { ...item, quantity };
        })
      );

      if (appliedClamp) {
        openCartNotice({
          severity: 'warning',
          title: 'Stock limit applied',
          message: 'Cart quantity was adjusted to match available stock.',
        });
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Clear the cart
  const clearCart = async () => {
    setLoading(true);
    try {
      setCartItems([]);
      if (isAuthenticated && user?.id) {
        await CartService.clearCartFromDatabase(user.id);
      } else {
        clearLocalStorageCart();
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Checkout process
  const checkout = async (shippingAddress: ShippingAddress, paymentDetails: PaymentDetails) => {
    if (!isAuthenticated || !user?.id) {
      return { success: false, error: 'Please log in to complete your order' };
    }

    if (cartItems.length === 0) {
      return { success: false, error: 'Your cart is empty' };
    }

    setLoading(true);
    try {
      const order = await CartService.createOrder(user.id, cartItems, shippingAddress, paymentDetails);
      
      // Clear cart after successful order
      setCartItems([]);
      
      return { success: true, orderId: order.id };
    } catch (error) {
      console.error('Checkout error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Checkout failed. Please try again.' 
      };
    } finally {
      setLoading(false);
    }
  };

  // Refresh cart from database
  const refreshCart = async () => {
    if (!isAuthenticated || !user?.id) return;
    
    setLoading(true);
    try {
      const dbCartItems = await CartService.loadCartFromDatabase(user.id);
      setCartItems(dbCartItems);
    } catch (error) {
      console.error('Error refreshing cart:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate the total price of all items in the cart
  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  // Get the total number of items in the cart
  const getItemCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  // Check if a product is in the cart
  const isInCart = (productId: string) => {
    return cartItems.some(item => item.id === productId);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      loading,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getItemCount,
      isInCart,
      checkout,
      refreshCart
    }}>
      {children}
      <Snackbar
        open={cartNotice.open}
        autoHideDuration={3500}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setCartNotice((prev) => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={cartNotice.severity}
          variant="filled"
          onClose={() => setCartNotice((prev) => ({ ...prev, open: false }))}
          sx={{
            width: '100%',
            minWidth: { xs: 'calc(100vw - 32px)', sm: 420 },
            borderRadius: 3,
            boxShadow: 6,
            alignItems: 'center',
            bgcolor: cartNotice.severity === 'success' ? 'success.dark' : 'warning.dark',
          }}
          action={
            <Button
              color="inherit"
              size="small"
              sx={{ fontWeight: 700 }}
              onClick={() => {
                setCartNotice((prev) => ({ ...prev, open: false }));
                window.location.assign('/checkout');
              }}
            >
              Checkout
            </Button>
          }
        >
          <Stack spacing={0.25}>
            <Typography variant="subtitle2" fontWeight={700}>
              {cartNotice.title}
            </Typography>
            <Typography variant="body2">
              {cartNotice.message}
            </Typography>
          </Stack>
        </Alert>
      </Snackbar>
    </CartContext.Provider>
  );
};
